import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import comptabiliteService, { AxeAnalytique, RapportAnalytique, TypeAxe } from '../../api/comptabilite';
import { formatTND } from '../../utils/formatters';

const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);
const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2';

const TYPE_LABEL: Record<TypeAxe, string> = {
  PROJET: 'Projet', DEPOT: 'Dépôt', ACTIVITE: 'Activité', AUTRE: 'Autre',
};

/**
 * Comptabilité analytique (TF-061) : référentiel d'axes (projet/dépôt/activité)
 * et rapport charges/produits/résultat ventilé par axe (classes 6 et 7).
 */
const Analytique: React.FC = () => {
  const [axes, setAxes] = useState<AxeAnalytique[]>([]);
  const [rapport, setRapport] = useState<RapportAnalytique | null>(null);
  const [startDate, setStartDate] = useState(startOfYear());
  const [endDate, setEndDate] = useState(today());
  const [form, setForm] = useState<{ code: string; libelle: string; type: TypeAxe }>({
    code: '', libelle: '', type: 'PROJET',
  });
  const [busy, setBusy] = useState(false);

  const loadAxes = () => comptabiliteService.listAxes().then(setAxes).catch(() => {});
  const loadRapport = () =>
    comptabiliteService.rapportAnalytique(startDate, endDate).then(setRapport)
      .catch((e: any) => toast.error(e?.response?.data?.message || 'Rapport indisponible.'));

  useEffect(() => { loadAxes(); loadRapport(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const createAxe = async () => {
    if (!form.code.trim() || !form.libelle.trim()) { toast.error('Code et libellé requis.'); return; }
    setBusy(true);
    try {
      await comptabiliteService.createAxe({ code: form.code.trim(), libelle: form.libelle.trim(), type: form.type });
      setForm({ code: '', libelle: '', type: 'PROJET' });
      toast.success('Axe créé.');
      loadAxes();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Création impossible.'); }
    finally { setBusy(false); }
  };

  const deleteAxe = async (a: AxeAnalytique) => {
    if (!window.confirm(`Supprimer l'axe ${a.code} ? L'historique imputé est conservé.`)) return;
    try { await comptabiliteService.deleteAxe(a.id); loadAxes(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Suppression impossible.'); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Comptabilité analytique</h1>
        <p className="text-sm text-gray-500">
          Axes (projet / dépôt / activité) et résultat par axe.{' '}
          <Link to="/comptabilite" className="text-blue-600 hover:underline">← Comptabilité</Link>
        </p>
      </div>

      {/* Référentiel des axes */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
        <h2 className="text-lg font-semibold">Axes analytiques</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Code</label>
            <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                   placeholder="PRJ-A" className={inputCls + ' w-32'} />
          </div>
          <div className="flex-1 min-w-48">
            <label className="block text-xs text-gray-600 mb-1">Libellé</label>
            <input value={form.libelle} onChange={(e) => setForm((f) => ({ ...f, libelle: e.target.value }))}
                   placeholder="Projet Atlas" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Type</label>
            <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as TypeAxe }))}
                    className={inputCls + ' w-36'}>
              {(Object.keys(TYPE_LABEL) as TypeAxe[]).map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
          </div>
          <button onClick={createAxe} disabled={busy}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
            Créer
          </button>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
            <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Libellé</th>
                <th className="px-4 py-2">Type</th><th className="px-4 py-2 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {axes.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                Aucun axe — créez-en pour ventiler vos charges et produits.
              </td></tr>
            )}
            {axes.map((a) => (
              <tr key={a.id}>
                <td className="px-4 py-2 font-mono text-xs">{a.code}</td>
                <td className="px-4 py-2">{a.libelle}{!a.actif && <span className="ml-2 text-xs text-gray-400">(inactif)</span>}</td>
                <td className="px-4 py-2 text-gray-500">{TYPE_LABEL[a.type]}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => deleteAxe(a)} className="text-red-600 hover:underline">Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Rapport par axe */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <h2 className="text-lg font-semibold flex-1">Résultat par axe</h2>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Du</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Au</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} />
          </div>
          <button onClick={loadRapport}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
            Actualiser
          </button>
        </div>

        {rapport && (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
              <tr><th className="px-4 py-2">Axe</th>
                  <th className="px-4 py-2 text-right">Charges (cl. 6)</th>
                  <th className="px-4 py-2 text-right">Produits (cl. 7)</th>
                  <th className="px-4 py-2 text-right">Résultat</th>
                  <th className="px-4 py-2 text-right">Lignes</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rapport.axes.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Aucune ligne de classe 6/7 sur la période.
                </td></tr>
              )}
              {rapport.axes.map((l, i) => (
                <tr key={i} className={l.axeCode == null ? 'bg-gray-50/60' : ''}>
                  <td className="px-4 py-2">
                    {l.axeCode == null
                      ? <span className="italic text-gray-500">{l.axeLibelle}</span>
                      : <><span className="font-mono text-xs mr-2">{l.axeCode}</span>{l.axeLibelle}</>}
                  </td>
                  <td className="px-4 py-2 text-right">{formatTND(l.charges)}</td>
                  <td className="px-4 py-2 text-right">{formatTND(l.produits)}</td>
                  <td className={`px-4 py-2 text-right font-semibold ${l.resultat < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                    {formatTND(l.resultat)}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-500">{l.nbLignes}</td>
                </tr>
              ))}
            </tbody>
            {rapport.axes.length > 0 && (
              <tfoot className="bg-gray-50 font-semibold">
                <tr className="border-t-2 border-gray-200">
                  <td className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 text-right">{formatTND(rapport.totalCharges)}</td>
                  <td className="px-4 py-2 text-right">{formatTND(rapport.totalProduits)}</td>
                  <td className={`px-4 py-2 text-right ${rapport.totalResultat < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                    {formatTND(rapport.totalResultat)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
};

export default Analytique;
